// The finish gantry: two braced timber towers across the finish line carrying a plum banner
// with the Cat Derby wordmark and chequered ends (both faces), lamps and pennants.
import { DoubleSide, Group, Mesh, MeshStandardMaterial, PlaneGeometry } from 'three';
import { GANTRY } from './layout';
import type { EnvMaterials } from './materials';
import { finishBanner } from './signs';
import { box, merge } from './util';

export class Gantry {
  readonly group = new Group();

  constructor(mats: EnvMaterials) {
    this.group.name = 'finish-gantry';
    const { x, towerZ, beamY } = GANTRY;
    const timber = [];
    const legH = beamY + 1.4;
    const bw = towerZ * 2 - 1.5, bh = bw * (288 / 2048) * 1.18;
    for (const side of [-1, 1]) {
      const z = side * towerZ;
      // Four legs, cross braces on each face and a small judge's platform.
      for (const dx of [-0.55, 0.55]) for (const dz of [-0.55, 0.55]) {
        timber.push(box(0.2, legH, 0.2, { at: [x + dx, legH / 2, z + dz], tile: 1.2, grain: 'y' }));
      }
      for (let level = 0; level < 3; level++) {
        const y0 = 0.6 + level * 2.15, y1 = y0 + 2.0;
        const mid = (y0 + y1) / 2, len = Math.hypot(1.1, y1 - y0), ang = Math.atan2(y1 - y0, 1.1);
        for (const dz of [-0.6, 0.6]) {
          timber.push(box(len, 0.1, 0.06, { at: [x, mid, z + dz], rot: [0, 0, level % 2 ? ang : -ang], tile: 1.2 }));
        }
        for (const dx of [-0.6, 0.6]) {
          timber.push(box(0.06, 0.1, len, { at: [x + dx, mid, z], rot: [level % 2 ? ang : -ang, 0, 0], tile: 1.2 }));
        }
        timber.push(box(1.3, 0.12, 1.3, { at: [x, y1, z], tile: 1.2, shade: 0.85 }));
      }
      // Roof cap.
      timber.push(box(1.6, 0.14, 1.6, { at: [x, legH + 0.05, z], tile: 1.2, shade: 0.8 }));
    }
    // Twin cross beams carrying the banner.
    const span = towerZ * 2 + 1.2;
    timber.push(box(0.24, 0.3, span, { at: [x - 0.18, beamY + 1.1, 0], tile: 1.6 }));
    timber.push(box(0.24, 0.3, span, { at: [x + 0.18, beamY + 1.1, 0], tile: 1.6 }));
    // Lower beam sits just under the banner so it never covers the FINISH line of text.
    timber.push(box(0.24, 0.2, span, { at: [x, beamY + 0.1 - bh / 2 - 0.2, 0], tile: 1.6, shade: 0.85 }));
    const frame = new Mesh(merge(timber), mats.timber);
    frame.castShadow = true; frame.receiveShadow = true;
    this.group.add(frame);

    // Banner, both faces (it reads correctly from either direction).
    const tex = finishBanner();
    const bannerMat = new MeshStandardMaterial({ map: tex, roughness: 0.8, side: DoubleSide });
    for (const face of [-1, 1]) {
      const g = new PlaneGeometry(bw, bh);
      g.rotateY(face < 0 ? -Math.PI / 2 : Math.PI / 2);
      const m = new Mesh(g, bannerMat);
      m.position.set(x + face * 0.03, beamY + 0.1, 0);
      m.castShadow = true; m.receiveShadow = true;
      this.group.add(m);
    }
    // Gold trim around the banner.
    const trim = new Mesh(merge([
      box(0.1, 0.08, bw + 0.1, { at: [x, beamY + 0.1 + bh / 2 + 0.04, 0] }),
      box(0.1, 0.08, bw + 0.1, { at: [x, beamY + 0.1 - bh / 2 - 0.04, 0] }),
    ]), mats.gold);
    this.group.add(trim);

    // Finish posts: tall white posts with a gold disc on the line itself, just outside the track.
    const posts = new Mesh(merge([
      box(0.12, 2.4, 0.12, { at: [x, 1.2, -towerZ + 1.15], tile: 1 }),
      box(0.12, 2.4, 0.12, { at: [x, 1.2, towerZ - 1.15], tile: 1 }),
    ]), mats.paintedCream);
    posts.castShadow = true;
    this.group.add(posts);
  }
}
